import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { navLinks, siteInfo } from "@/mocks/site";

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 24);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !transparent || scrolled || menuOpen;

  const shellClass = solid
    ? "bg-background-50/95 backdrop-blur-md border-b border-background-200"
    : "bg-transparent border-b border-transparent";

  const brandTextClass = solid ? "text-foreground-950" : "text-background-50";
  const brandSubClass = solid ? "text-foreground-500" : "text-background-200";

  const linkBase = solid
    ? "text-foreground-600 hover:text-primary-600"
    : "text-background-100 hover:text-background-50";
  const linkActive = solid ? "text-primary-600" : "text-background-50";

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${shellClass}`}
    >
      <div className="w-full px-4 md:px-6 h-[72px] flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 cursor-pointer group shrink-0">
          <span
            className={`w-10 h-10 flex items-center justify-center rounded-md transition-colors ${
              solid ? "bg-primary-500 text-background-50" : "bg-background-50/20 text-background-50"
            }`}
          >
            <i className="ri-eye-line text-xl" aria-hidden="true"></i>
          </span>
          <span className="flex flex-col leading-none">
            <span className={`font-heading text-lg md:text-xl font-bold tracking-wide ${brandTextClass}`}>
              {siteInfo.name}
            </span>
            <span className={`text-[11px] mt-1 tracking-wider ${brandSubClass}`}>
              网暴黑料调查 · 舆论解剖
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
          {navLinks.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === "/"}
              className={({ isActive }) =>
                `px-4 py-2 rounded-md text-sm font-medium whitespace-nowrap transition-colors ${
                  isActive ? linkActive : linkBase
                }`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer transition-colors bg-primary-500 text-background-50 hover:bg-primary-600"
          >
            <i className="ri-mail-send-line" aria-hidden="true"></i>
            提供线索
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((prev) => !prev)}
          aria-label={menuOpen ? "关闭导航菜单" : "打开导航菜单"}
          aria-expanded={menuOpen}
          className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-md cursor-pointer transition-colors ${
            solid
              ? "text-foreground-800 hover:bg-background-100"
              : "text-background-50 hover:bg-background-50/15"
          }`}
        >
          <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
        </button>
      </div>

      {menuOpen && (
        <div className="lg:hidden bg-background-50 border-t border-background-200 shadow-sm">
          <nav aria-label="移动端导航" className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-sm font-medium transition-colors ${
                    isActive
                      ? "bg-primary-100 text-primary-700"
                      : "text-foreground-700 hover:bg-background-100"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md text-sm font-medium whitespace-nowrap cursor-pointer bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-send-line" aria-hidden="true"></i>
              提供线索
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}